import React, { useEffect, useState } from "react";
import { supabase } from "../../supabaseClient";
import { format, addDays } from "date-fns";
import { useProgram } from "../../context/ProgramContext";

export default function WeeklyWorkouts({ userId }) {
  const { selectedProgram } = useProgram();
  const programId = selectedProgram?.id?.trim();

  const [userProgram, setUserProgram] = useState(null);
  const [workouts, setWorkouts] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [programWeeks, setProgramWeeks] = useState([]);
  const [expandedWeeks, setExpandedWeeks] = useState({});

  // Wait for programId
  useEffect(() => {
    if (!userId || !programId) return;

    async function fetchUserProgram() {
      try {
        const { data, error } = await supabase
          .from("user_exercise_programs")
          .select("*")
          .eq("athlete_id", userId)
          .eq("followed_program", programId);

        if (error) throw error;

        // Filter by today between start_date and end_date in JS
        const today = new Date();
        const activeProgram = data.find((row) => {
          const start = new Date(row.start_date);
          const end = new Date(row.end_date);
          return today >= start && today <= end;
        });

        if (activeProgram) setUserProgram(activeProgram);
      } catch (err) {
        console.error("Error fetching user program:", err);
      }
    }

    fetchUserProgram();
  }, [userId, programId]);

  // Compute program weeks
  useEffect(() => {
    if (!userProgram) return;

    const start = new Date(userProgram.start_date);
    const end = new Date(userProgram.end_date);

    let weeks = [];
    let weekStart = start;
    while (weekStart <= end) {
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      weeks.push({ start: new Date(weekStart), end: weekEnd });
      weekStart = addDays(weekStart, 7);
    }

    setProgramWeeks(weeks);

    // Expand current week by default
    const today = new Date();
    const currentWeekIndex = weeks.findIndex(
      (w) => today >= w.start && today <= w.end
    );
    if (currentWeekIndex !== -1) {
      setExpandedWeeks({ [currentWeekIndex]: true });
      setSelectedDate(today);
    } else {
      setExpandedWeeks({ [0]: true });
      setSelectedDate(weeks[0].start);
    }
  }, [userProgram]);

  // Fetch workouts
  useEffect(() => {
    if (!programId || !userProgram) return;

    async function fetchWorkouts() {
      try {
        const { data, error } = await supabase
          .from("daily_exercise_drills")
          .select("*")
          .eq("followed_program", programId)
          .gte("drill_date", format(new Date(userProgram.start_date), "yyyy-MM-dd"))
          .lte("drill_date", format(new Date(userProgram.end_date), "yyyy-MM-dd"))
          .order("order_index", { ascending: true });

        if (error) throw error;
        console.log(data)
        setWorkouts(data || []);
      } catch (err) {
        console.error("Error fetching workouts:", err);
      }
    }

    fetchWorkouts();
  }, [programId, userProgram]);

  const selectedDateStr = format(selectedDate, "yyyy-MM-dd");
  const filteredWorkouts = workouts.filter(
    (w) => format(new Date(w.drill_date + "T00:00:00"), "yyyy-MM-dd") === selectedDateStr
  );

  const toggleWeek = (index) => {
    setExpandedWeeks((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  if (!programId) return <p className="p-6 text-gray-500">No program selected.</p>;
  if (!userProgram) return <p className="p-6 text-gray-500">No active program for this user.</p>;

  return (
    <div className="flex min-h-screen bg-gray-100">
      <aside className="w-64 bg-white shadow-md p-4 overflow-y-auto">
        <h2 className="text-lg font-semibold mb-2">Program Weeks</h2>
        <ul className="space-y-2">
          {programWeeks.map((week, index) => {
            const isExpanded = !!expandedWeeks[index];
            const weekLabel = `${format(week.start, "MMM d")} - ${format(week.end, "MMM d")}`;

            const daysInWeek = [];
            for (let d = 0; d < 7; d++) {
              const day = addDays(week.start, d);
              if (day > new Date(userProgram.end_date)) break;
              daysInWeek.push(day);
            }

            return (
              <li key={weekLabel}>
                <button
                  onClick={() => toggleWeek(index)}
                  className="w-full text-left px-3 py-2 rounded-md font-semibold bg-gray-200 hover:bg-gray-300"
                >
                  {weekLabel} {isExpanded ? "▼" : "▶"}
                </button>
                {isExpanded && (
                  <ul className="pl-4 mt-1 space-y-1">
                    {daysInWeek.map((date) => {
                      const formatted = format(date, "EEE, MMM d");
                      const isSelected = format(date, "yyyy-MM-dd") === selectedDateStr;
                      return (
                        <li key={formatted}>
                          <button
                            onClick={() => setSelectedDate(date)}
                            className={`w-full text-left px-2 py-1 rounded-md ${
                              isSelected
                                ? "bg-blue-500 text-white font-semibold"
                                : "hover:bg-gray-100 text-gray-700"
                            }`}
                          >
                            {formatted}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </aside>

      <main className="flex-1 p-6">
        <h1 className="text-2xl font-bold mb-4">
          Workouts for {format(selectedDate, "EEEE, MMMM d")}
        </h1>

        {filteredWorkouts.length > 0 ? (
          <div className="grid gap-4">
            {filteredWorkouts.map((w) => (
              <div
                key={w.id}
                className="bg-white p-4 rounded-lg shadow hover:shadow-lg transition"
              >
                <h3 className="text-lg font-semibold">{w.drill_name}</h3>
                <p className="text-gray-600">{w.description}</p>
                <p className="text-sm text-gray-500 mt-1">
                  Duration/Reps: {w.duration_or_reps}
                </p>
                {w.notes && <p className="mt-1 text-gray-500 italic">Notes: {w.notes}</p>}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 italic">No workouts scheduled for this day.</p>
        )}
      </main>
    </div>
  );
}
